import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  CheckCircle, 
  AlertCircle,
  Info,
  Save,
  Send,
  User,
  Loader2,
  Shield,
  Stethoscope,
  Calendar,
  Archive
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/services/api';
import { useApp } from '@/context/AppContext';

export default function NouvelleAutorisation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useApp();
  const preselectedCollaborateur = location.state?.collaborateur;

  // Data from API
  const [collaborateurs, setCollaborateurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Collaborateur specific data
  const [collaborateurCACES, setCollaborateurCACES] = useState([]);
  const [collaborateurVisites, setCollaborateurVisites] = useState([]);
  const [collaborateurAutorisations, setCollaborateurAutorisations] = useState([]);
  const [loadingCollabData, setLoadingCollabData] = useState(false);
  const [showArchivedCaces, setShowArchivedCaces] = useState(false);
  const [showArchivedVisites, setShowArchivedVisites] = useState(false);
  const [validationWarning, setValidationWarning] = useState(null);

  const [formData, setFormData] = useState({
    collaborateurId: preselectedCollaborateur?.id || '',
    selectedCacesIds: [],
    selectedVisiteId: '',
    notesHSE: ''
  });

  // Calculated dates
  const [dateDebut, setDateDebut] = useState(new Date().toISOString().split('T')[0]);
  const [dateFin, setDateFin] = useState('');
  const [canSubmit, setCanSubmit] = useState(false);

  // Load initial data - filter collaborateurs for Manager role
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const collabsData = await api.getCollaborateurs();
        
        // If current user is a Manager, filter to show only their team members
        if (currentUser?.role === 'Manager') {
          const filteredCollabs = (collabsData || []).filter(
            collab => collab.manager_id === currentUser.id
          );
          setCollaborateurs(filteredCollabs);
        } else {
          setCollaborateurs(collabsData || []);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [currentUser]);

  // Load collaborateur's CACES and Visites when selected
  useEffect(() => {
    const loadCollaborateurData = async () => {
      if (!formData.collaborateurId) {
        setCollaborateurCACES([]);
        setCollaborateurVisites([]);
        return;
      }

      try {
        setLoadingCollabData(true);
        const [cacesData, visitesData, autorisationsData] = await Promise.all([
          api.getCollaborateurCACES(formData.collaborateurId),
          api.getCollaborateurVisites(formData.collaborateurId),
          api.getCollaborateurAutorisations(formData.collaborateurId)
        ]);
        
        // Store all data (including archived) - filtering will be done in display
        setCollaborateurCACES(cacesData || []);
        setCollaborateurVisites(visitesData || []);
        setCollaborateurAutorisations(autorisationsData || []);
        
        // Reset selections and archive toggles when collaborateur changes
        setFormData(prev => ({
          ...prev,
          selectedCacesIds: [],
          selectedVisiteId: ''
        }));
        setDateFin('');
        setShowArchivedCaces(false);
        setShowArchivedVisites(false);
        setValidationWarning(null);
      } catch (error) {
        console.error('Error loading collaborateur data:', error);
        // Check if this is an access error for managers
        if (error.message && (error.message.includes('403') || error.message.includes('non autorisé') || error.message.includes('Forbidden'))) {
          toast.error('Vous ne pouvez créer des demandes que pour vos propres collaborateurs. Ce collaborateur n\'est pas sous votre responsabilité.');
        } else {
          toast.error('Erreur lors du chargement des données du collaborateur');
        }
      } finally {
        setLoadingCollabData(false);
      }
    };
    
    loadCollaborateurData();
  }, [formData.collaborateurId]);

  // Filter CACES: valid (not expired) + not archived (unless toggle is ON)
  const filteredCACES = collaborateurCACES.filter(c => {
    const today = new Date();
    const isValid = c.date_fin && new Date(c.date_fin) > today;
    const isArchived = c.is_archived === true;
    
    if (!isValid) return false; // Always hide expired
    if (isArchived && !showArchivedCaces) return false; // Hide archived unless toggle is ON
    return true;
  });

  // Filter Visites: valid (not expired) + not archived (unless toggle is ON)
  const filteredVisites = collaborateurVisites.filter(v => {
    const today = new Date();
    const isValid = v.date_fin && new Date(v.date_fin) > today;
    const isArchived = v.is_archived === true;
    
    if (!isValid) return false; // Always hide expired
    if (isArchived && !showArchivedVisites) return false; // Hide archived unless toggle is ON
    return true;
  });

  // Count archived items for display
  const archivedCacesCount = collaborateurCACES.filter(c => {
    const today = new Date();
    const isValid = c.date_fin && new Date(c.date_fin) > today;
    return isValid && c.is_archived === true;
  }).length;

  const archivedVisitesCount = collaborateurVisites.filter(v => {
    const today = new Date();
    const isValid = v.date_fin && new Date(v.date_fin) > today;
    return isValid && v.is_archived === true;
  }).length;

  // Calculate date_fin based on selected CACES and Visite
  useEffect(() => {
    if (formData.selectedCacesIds.length === 0 || !formData.selectedVisiteId) {
      setDateFin('');
      setCanSubmit(false);
      return;
    }

    // Get selected CACES dates (from all CACES, not just filtered)
    const selectedCaces = collaborateurCACES.filter(c => formData.selectedCacesIds.includes(c.id));
    const cacesDates = selectedCaces.map(c => new Date(c.date_fin)).filter(d => !isNaN(d));
    
    // Get selected visite date
    const selectedVisite = collaborateurVisites.find(v => v.id === formData.selectedVisiteId);
    const visiteDate = selectedVisite ? new Date(selectedVisite.date_fin) : null;

    if (cacesDates.length === 0 || !visiteDate) {
      setDateFin('');
      setCanSubmit(false);
      return;
    }

    // Find minimum date (earliest expiration)
    const allDates = [...cacesDates, visiteDate];
    const minDate = new Date(Math.min(...allDates));
    
    const calculatedDateFin = minDate.toISOString().split('T')[0];
    setDateFin(calculatedDateFin);
    
    // Validation préventive : Vérifier si une autorisation similaire existe déjà
    const autorisationsActives = collaborateurAutorisations.filter(
      auth => auth.statut === 'Délivrée'
    );
    
    if (autorisationsActives.length > 0) {
      // Récupérer toutes les catégories existantes
      const categoriesExistantes = new Set();
      autorisationsActives.forEach(auth => {
        (auth.categories_caces_ids || []).forEach(catId => categoriesExistantes.add(catId));
      });
      
      // Catégories de la nouvelle demande
      const nouvellesCategories = new Set(
        selectedCaces.map(c => c.categorie_id).filter(Boolean)
      );
      
      // Vérifier s'il y a de nouvelles catégories
      const categoriesAjoutees = [...nouvellesCategories].filter(
        cat => !categoriesExistantes.has(cat)
      );
      
      // Trouver la date de fin max existante
      const dateFinMax = autorisationsActives.reduce((max, auth) => {
        return auth.date_fin > max ? auth.date_fin : max;
      }, '');
      
      const dateProlongee = calculatedDateFin > dateFinMax;
      
      // Afficher un avertissement si c'est un doublon potentiel
      if (categoriesAjoutees.length === 0 && !dateProlongee) {
        setValidationWarning({
          type: 'error',
          message: `Ce collaborateur possède déjà une autorisation valide avec les mêmes catégories et une date de validité égale ou supérieure (${dateFinMax}). Ajoutez de nouvelles catégories ou prolongez la date de validité.`
        });
        setCanSubmit(false);
        return;
      } else if (categoriesAjoutees.length > 0) {
        setValidationWarning({
          type: 'info',
          message: `✓ Nouvelles catégories détectées. Les anciennes autorisations seront automatiquement archivées lors de la validation.`
        });
      } else if (dateProlongee) {
        setValidationWarning({
          type: 'info',
          message: `✓ Date de validité prolongée (${dateFinMax} → ${calculatedDateFin}). L'ancienne autorisation sera automatiquement archivée lors de la validation.`
        });
      } else {
        setValidationWarning(null);
      }
    } else {
      setValidationWarning(null);
    }
    
    setCanSubmit(true);
  }, [formData.selectedCacesIds, formData.selectedVisiteId, collaborateurCACES, collaborateurVisites, collaborateurAutorisations]);

  const handleCacesToggle = (cacesId) => {
    setFormData(prev => ({
      ...prev,
      selectedCacesIds: prev.selectedCacesIds.includes(cacesId)
        ? prev.selectedCacesIds.filter(id => id !== cacesId)
        : [...prev.selectedCacesIds, cacesId]
    }));
  };

  const handleSaveDraft = async () => {
    if (!formData.collaborateurId) {
      toast.error('Veuillez sélectionner un collaborateur');
      return;
    }

    try {
      setSaving(true);
      
      // Get categories from selected CACES
      const selectedCaces = collaborateurCACES.filter(c => formData.selectedCacesIds.includes(c.id));
      const categoriesIds = [...new Set(selectedCaces.map(c => c.categorie_id).filter(Boolean))];
      
      const payload = {
        collaborateur_id: formData.collaborateurId,
        categories_caces_ids: categoriesIds,
        caces_ids: formData.selectedCacesIds,
        visite_medicale_id: formData.selectedVisiteId || '',
        date_debut: dateDebut,
        date_fin: dateFin || dateDebut,
        notes_hse: formData.notesHSE
      };

      await api.createAutorisation(payload, currentUser?.id, `${currentUser?.prenom} ${currentUser?.nom}`);
      toast.success('Brouillon enregistré');
      navigate('/demandes');
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error(error.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error('Veuillez sélectionner au moins un CACES valide et une visite médicale valide');
      return;
    }

    try {
      setSaving(true);
      
      // Get categories from selected CACES
      const selectedCaces = collaborateurCACES.filter(c => formData.selectedCacesIds.includes(c.id));
      const categoriesIds = [...new Set(selectedCaces.map(c => c.categorie_id).filter(Boolean))];
      
      const payload = {
        collaborateur_id: formData.collaborateurId,
        categories_caces_ids: categoriesIds,
        caces_ids: formData.selectedCacesIds,
        visite_medicale_id: formData.selectedVisiteId,
        date_debut: dateDebut,
        date_fin: dateFin,
        notes_hse: formData.notesHSE
      };

      const result = await api.createAutorisation(payload, currentUser?.id, `${currentUser?.prenom} ${currentUser?.nom}`);
      
      // Submit for HSE validation
      if (result?.id) {
        await api.submitAutorisation(result.id);
      }
      
      toast.success('Demande d\'autorisation soumise pour validation HSE');
      navigate('/demandes');
    } catch (error) {
      console.error('Error submitting:', error);
      toast.error(error.message || 'Erreur lors de la soumission');
    } finally {
      setSaving(false);
    }
  };

  const selectedCollaborateur = collaborateurs.find(c => c.id === formData.collaborateurId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div>
        <Link to="/demandes">
          <Button variant="ghost" className="gap-2 mb-4">
            <ArrowLeft className="w-4 h-4" />
            Retour aux demandes
          </Button>
        </Link>
        <h1 className="text-3xl font-bold text-foreground">Nouvelle autorisation de conduite</h1>
        <p className="text-muted-foreground mt-1">Créer une demande d'autorisation interne CACES</p>
      </div>

      {/* Info alert */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Une autorisation sera délivrée uniquement si le collaborateur possède un <strong>CACES valide</strong> ET une <strong>visite médicale valide</strong>.
          La date de fin d'autorisation est automatiquement calculée (minimum des deux dates d'expiration).
        </AlertDescription>
      </Alert>

      {/* Collaborateur selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            Sélection du collaborateur
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="collaborateur">Collaborateur *</Label>
            <Select 
              value={formData.collaborateurId} 
              onValueChange={(value) => {
                const collab = collaborateurs.find(c => c.id === value);
                setFormData(prev => ({ 
                  ...prev, 
                  collaborateurId: value
                }));
              }}
            >
              <SelectTrigger id="collaborateur">
                <SelectValue placeholder="Sélectionnez un collaborateur" />
              </SelectTrigger>
              <SelectContent>
                {collaborateurs.map(collab => (
                  <SelectItem key={collab.id} value={collab.id}>
                    {collab.prenom} {collab.nom} - {collab.matricule}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedCollaborateur && (
            <div className="p-4 rounded-lg bg-accent">
              <p className="text-sm font-medium text-foreground mb-2">Informations collaborateur</p>
              <div className="grid gap-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Poste:</span>
                  <span className="font-medium">{selectedCollaborateur.poste || 'Non défini'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Secteur:</span>
                  <span className="font-medium">{selectedCollaborateur.secteur || 'Non défini'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email:</span>
                  <span className="font-medium">{selectedCollaborateur.email}</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* CACES Selection */}
      {formData.collaborateurId && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  CACES valides du collaborateur
                </CardTitle>
                <CardDescription>
                  Sélectionnez les CACES à inclure dans l'autorisation
                </CardDescription>
              </div>
              {archivedCacesCount > 0 && (
                <div className="flex items-center gap-2">
                  <Switch
                    id="show-archived-caces"
                    checked={showArchivedCaces}
                    onCheckedChange={setShowArchivedCaces}
                  />
                  <Label htmlFor="show-archived-caces" className="text-sm flex items-center gap-1 cursor-pointer">
                    <Archive className="h-4 w-4" />
                    Voir les archives ({archivedCacesCount})
                  </Label>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loadingCollabData ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : filteredCACES.length === 0 ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Ce collaborateur n'a aucun CACES valide{archivedCacesCount > 0 ? ' (hors archives)' : ''}. Veuillez d'abord ajouter un CACES dans sa fiche.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-3">
                {filteredCACES.map((caces) => (
                  <div
                    key={caces.id}
                    className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-colors ${
                      formData.selectedCacesIds.includes(caces.id) 
                        ? 'border-primary bg-primary/5' 
                        : caces.is_archived 
                          ? 'bg-gray-50 opacity-80 hover:bg-gray-100' 
                          : 'hover:bg-accent'
                    }`}
                    onClick={() => handleCacesToggle(caces.id)}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={formData.selectedCacesIds.includes(caces.id)}
                        onCheckedChange={() => handleCacesToggle(caces.id)}
                      />
                      <div>
                        <p className="font-semibold flex items-center gap-2">
                          {caces.categorie_code || caces.categorie}
                          {caces.is_archived && (
                            <Badge variant="secondary" className="text-xs">
                              <Archive className="h-3 w-3 mr-1" />
                              Archivé
                            </Badge>
                          )}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          N° {caces.numero_certificat || 'N/A'} - {caces.organisme || 'Organisme non spécifié'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Valide
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1">
                        Expire le {new Date(caces.date_fin).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Visite Médicale Selection */}
      {formData.collaborateurId && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Stethoscope className="w-5 h-5 text-green-600" />
                  Visite médicale valide
                </CardTitle>
                <CardDescription>
                  Sélectionnez la visite médicale de référence
                </CardDescription>
              </div>
              {archivedVisitesCount > 0 && (
                <div className="flex items-center gap-2">
                  <Switch
                    id="show-archived-visites"
                    checked={showArchivedVisites}
                    onCheckedChange={setShowArchivedVisites}
                  />
                  <Label htmlFor="show-archived-visites" className="text-sm flex items-center gap-1 cursor-pointer">
                    <Archive className="h-4 w-4" />
                    Voir les archives ({archivedVisitesCount})
                  </Label>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loadingCollabData ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : filteredVisites.length === 0 ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Ce collaborateur n'a aucune visite médicale valide{archivedVisitesCount > 0 ? ' (hors archives)' : ''}. Veuillez d'abord ajouter une visite médicale dans sa fiche.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-3">
                {filteredVisites.map((visite) => (
                  <div
                    key={visite.id}
                    className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-colors ${
                      formData.selectedVisiteId === visite.id 
                        ? 'border-primary bg-primary/5' 
                        : visite.is_archived
                          ? 'bg-gray-50 opacity-80 hover:bg-gray-100'
                          : 'hover:bg-accent'
                    }`}
                    onClick={() => setFormData(prev => ({ ...prev, selectedVisiteId: visite.id }))}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        checked={formData.selectedVisiteId === visite.id}
                        onChange={() => setFormData(prev => ({ ...prev, selectedVisiteId: visite.id }))}
                        className="h-4 w-4"
                      />
                      <div>
                        <p className="font-semibold flex items-center gap-2">
                          {visite.type_visite_libelle || visite.type || 'Visite médicale'}
                          {visite.is_archived && (
                            <Badge variant="secondary" className="text-xs">
                              <Archive className="h-3 w-3 mr-1" />
                              Archivée
                            </Badge>
                          )}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Date: {visite.date_visite ? new Date(visite.date_visite).toLocaleDateString('fr-FR') : 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Valide
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1">
                        Expire le {new Date(visite.date_fin).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dates and Summary */}
      {formData.collaborateurId && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-600" />
              Dates de l'autorisation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Date de début</Label>
                <Input
                  type="date"
                  value={dateDebut}
                  onChange={(e) => setDateDebut(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Date de fin (calculée automatiquement)</Label>
                <Input
                  type="date"
                  value={dateFin}
                  disabled
                  className="bg-muted"
                />
                {dateFin && (
                  <p className="text-xs text-muted-foreground">
                    Basée sur la date d'expiration la plus proche (CACES ou visite médicale)
                  </p>
                )}
              </div>
            </div>

            {/* Validation Warning */}
            {validationWarning && (
              <Alert variant={validationWarning.type === 'error' ? 'destructive' : 'default'} className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {validationWarning.message}
                </AlertDescription>
              </Alert>
            )}

            {/* Summary */}
            {formData.selectedCacesIds.length > 0 && formData.selectedVisiteId && !validationWarning?.type === 'error' && (
              <div className="mt-6 p-4 rounded-lg bg-green-50 border border-green-200">
                <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                  <CheckCircle className="h-5 w-5" />
                  Autorisation prête à être soumise
                </div>
                <ul className="text-sm text-green-600 space-y-1">
                  <li>• {formData.selectedCacesIds.length} CACES sélectionné(s)</li>
                  <li>• 1 visite médicale sélectionnée</li>
                  <li>• Date de fin: {dateFin ? new Date(dateFin).toLocaleDateString('fr-FR') : 'Non calculée'}</li>
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Notes */}
      {formData.collaborateurId && (
        <Card>
          <CardHeader>
            <CardTitle>Notes (optionnel)</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={formData.notesHSE}
              onChange={(e) => setFormData(prev => ({ ...prev, notesHSE: e.target.value }))}
              placeholder="Ajoutez des notes ou commentaires pour cette autorisation..."
              rows={3}
            />
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-4">
        <Button 
          variant="outline" 
          onClick={handleSaveDraft}
          disabled={!formData.collaborateurId || saving}
        >
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Enregistrer brouillon
        </Button>
        <Button 
          onClick={handleSubmit}
          disabled={!canSubmit || saving}
        >
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
          Soumettre pour validation
        </Button>
      </div>
    </div>
  );
}
